import axios from "axios";
import logging from "./logging";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function subscribeToPush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    logging.info("Push not supported");
    return false;
  }

  try {
    const reg = await navigator.serviceWorker.ready;

    // Check existing subscription
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
      // Already subscribed — sync to backend
      await sendSubscriptionToBackend(sub);
      return true;
    }

    // Get VAPID key from backend or env
    let vapidKey = VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      const res = await axios.get(`${API}/push/vapid-key`);
      vapidKey = res.data.public_key;
    }
    if (!vapidKey) {
      logging.warn("No VAPID key available");
      return false;
    }

    // Request permission ONLY when it hasn't been decided yet. If the
    // browser already has an explicit state (`granted` or `denied`) we
    // respect it — calling `Notification.requestPermission()` in a non-
    // gesture context on a granted browser is technically a no-op but
    // has caused subtle warnings in the past, and on `denied` it would
    // silently fail. This makes `subscribeToPush()` safe to invoke from
    // a mount effect without a click when permission was granted on an
    // earlier visit.
    let permission = "Notification" in window ? Notification.permission : "denied";
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") {
      logging.info(`Push permission not granted (${permission})`);
      return false;
    }

    // Subscribe
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    await sendSubscriptionToBackend(sub);
    logging.info("Push subscription created");
    return true;
  } catch (err) {
    logging.warn("Push subscription failed", err);
    return false;
  }
}

async function sendSubscriptionToBackend(sub) {
  const subJson = sub.toJSON();
  await axios.post(
    `${API}/push/subscribe`,
    { endpoint: subJson.endpoint, keys: subJson.keys },
    { withCredentials: true }
  );
}

export async function unsubscribeFromPush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const subJson = sub.toJSON();
      await axios.post(
        `${API}/push/unsubscribe`,
        { endpoint: subJson.endpoint, keys: subJson.keys },
        { withCredentials: true }
      );
      await sub.unsubscribe();
    }
  } catch (err) {
    logging.warn("Push unsubscribe failed", err);
  }
}
